import { Drawer, Box, IconButton, Typography, Stack, Avatar, Chip, alpha, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import PartnerReviewDetail from '../documents/PartnerReviewDetail';
import type { PartnerRow } from '../../services/partnerUserService';
import { formatEmail, formatPhone } from '../../utils/contactMask';
import { useMaskContactSetting } from '../../hooks/useMaskContactSetting';
import { entityLabel, zoneName } from '../../utils/documentReviewUtils';

interface PartnerDocumentsPanelProps {
  open: boolean;
  onClose: () => void;
  partner: PartnerRow | null;
  entityType: string;
}

export default function PartnerDocumentsPanel({ open, onClose, partner, entityType }: PartnerDocumentsPanelProps) {
  const theme = useTheme();
  const { maskEnabled } = useMaskContactSetting();
  const initials = partner
    ? `${partner.firstName?.[0] || ''}${partner.lastName?.[0] || ''}`.toUpperCase() || '?'
    : '?';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '92vw', md: 1040, lg: 1120 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          borderInlineStart: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'light'
            ? '-16px 0 40px rgba(11,18,32,0.08)'
            : '-16px 0 40px rgba(0,0,0,0.4)',
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          borderTop: `3px solid ${theme.palette.primary.main}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" minWidth={0}>
          <Avatar sx={{ width: 44, height: 44, fontWeight: 700, bgcolor: 'primary.main' }}>{initials}</Avatar>
          <Box minWidth={0}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Documents
            </Typography>
            <Typography variant="h6" fontWeight={650} letterSpacing="-0.02em" noWrap>
              {partner ? `${partner.firstName} ${partner.lastName || ''}`.trim() : 'Partner'}
            </Typography>
            {partner && (
              <>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {formatEmail(partner.email, maskEnabled)} · {formatPhone(partner.phone, maskEnabled)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  <Chip size="small" label={entityLabel(entityType)} />
                  {partner.zoneId && <Chip size="small" variant="outlined" label={zoneName(partner.zoneId)} />}
                  {!!partner.pendingDocCount && (
                    <Chip size="small" color="warning" label={`${partner.pendingDocCount} pending`} />
                  )}
                </Stack>
              </>
            )}
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ mt: -0.25, bgcolor: alpha(theme.palette.text.primary, 0.04) }}
          aria-label="Close"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
        <PartnerReviewDetail partner={partner} entityType={entityType} embedded />
      </Box>
    </Drawer>
  );
}
